import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import carImg from '~/assets/landing/car.jpg';
import carwinImg from '~/assets/landing/carwin.jpg';
import bonusImg from '~/assets/landing/bonus.jpg';
import brandImg from '~/assets/landing/brand.jpg';
import bizImg from '~/assets/landing/biz.jpg';
import gadgetsImg from '~/assets/landing/gadgets.jpg';
import { useTilt } from './effects/use-tilt';
import { Countdown } from './countdown';
import { GOATS_PER_CHANCE, LANDING_DATES } from './landing-config';
import type { PrizeModalKey } from './demo-prizes';
import { LockedBadge } from './locked-badge';

interface PrizesProps {
  /** Участие не подтверждено — баланс и шкала до автомобиля закрыты замочком. */
  locked: boolean;
  balance: number | null;
  goatsTotal: number | null;
  onOpenPrizeModal: (key: PrizeModalKey) => void;
}

const SIDE_CARDS = [
  { key: 'bonus', img: bonusImg, delay: 'd1', titleKey: 'prizes.cards.bonus.title', subtitleKey: 'prizes.cards.bonus.subtitle' },
  { key: 'brand', img: brandImg, delay: 'd2', titleKey: 'prizes.cards.brand.title', subtitleKey: 'prizes.cards.brand.subtitle' },
  { key: 'biz', img: bizImg, delay: 'd3', titleKey: 'prizes.cards.biz.title', subtitleKey: 'prizes.cards.biz.subtitle' },
  { key: 'tech', img: gadgetsImg, delay: 'd4', titleKey: 'prizes.cards.tech.title', subtitleKey: 'prizes.cards.tech.subtitle' },
] as const;

/** Блок призов (`section#prizes` макета): 4 карточки + прогресс-шкала до авто + беспроигрышная лотерея. */
export function Prizes({ locked, balance, goatsTotal, onOpenPrizeModal }: PrizesProps) {
  const { t } = useTranslation('landing');
  const mainRef = useRef<HTMLElement>(null);
  useTilt(mainRef, 6);

  const earned = goatsTotal ?? 0;
  const left = Math.max(0, GOATS_PER_CHANCE - earned);
  const chances = Math.max(1, Math.floor(earned / GOATS_PER_CHANCE) || 1);

  return (
    <section className="sec s-beige round" id="prizes">
      <div className="wrap">
        <h2 className="h2 rv">{t('prizes.title')}</h2>
        <div className="prizes">
          <article className="pcard main tilt rv" ref={mainRef} data-prize="car" onClick={() => onOpenPrizeModal('car')}>
            <div className="pic">
              <img src={carImg} alt="Haval Jolion" />
            </div>
            <div className="info">
              <span className="tag">{t('prizes.main.tag')}</span>
              <h3>
                {t('prizes.main.title')} <span className="sup">{t('prizes.main.titleSup')}</span>
              </h3>
              <span className="cost big">
                <i className="ico i-goat" />
                {t('prizes.main.cost')}
              </span>
              <p>{t('prizes.main.text')}</p>
              <div className="maindraw">
                <div className="mdate">
                  <span className="mcal">
                    <b>5</b>
                    <i>янв</i>
                  </span>
                  <div>
                    <small>{t('prizes.main.finalLabel')}</small>
                    <strong>{t('prizes.main.finalDate')}</strong>
                    <em>{t('prizes.main.finalPlace')}</em>
                  </div>
                </div>
                <Countdown targetIso={LANDING_DATES.finalDraw} className="mcd" />
              </div>
              <a className="btn light magnet" href="#play" style={{ alignSelf: 'flex-start' }} onClick={(e) => e.stopPropagation()}>
                {t('prizes.main.myChances')}
              </a>
            </div>
            <i className="glare" />
          </article>

          {SIDE_CARDS.map(({ key, img, delay, titleKey, subtitleKey }) => (
            <SideCard key={key} prizeKey={key} img={img} delay={delay} titleKey={titleKey} subtitleKey={subtitleKey} onOpen={onOpenPrizeModal} />
          ))}
        </div>

        <div className="game" id="play" style={{ marginTop: 24 }}>
          <div className="balance rv">
            <i className="corner" style={{ left: 16, top: 16 }} />
            <i className="corner" style={{ right: 16, top: 16 }} />
            <i className="corner" style={{ left: 16, bottom: 16 }} />
            <i className="corner" style={{ right: 16, bottom: 16 }} />
            <h3>{t('prizes.balance.title')}</h3>
            {locked ? (
              <LockedBadge hint />
            ) : (
              <>
                <div className="balrow">
                  <i className="ico i-run" />
                  <span className="balnum">{balance ?? 0}</span>
                  <i className="ico i-run" />
                </div>
                <small>
                  {t('prizes.balance.noteLine1')}
                  <br />
                  {t('prizes.balance.noteLine2')}
                </small>
              </>
            )}
          </div>
          <div className="car rv d1" id="car">
            <div className="pic">
              <img src={carwinImg} alt="Haval Jolion" />
              {!locked && <span className="tag">{t('prizes.car.chances', { count: chances })}</span>}
            </div>
            <div className="body">
              <h3>{t('prizes.car.title')}</h3>
              {locked ? (
                <div className="lockmeter">
                  <LockedBadge hint />
                </div>
              ) : (
                <>
                  <div className="meter">
                    {Array.from({ length: GOATS_PER_CHANCE }, (_, i) => (
                      <i key={i} className={i < earned ? 'ico i-run on' : 'ico i-run'} />
                    ))}
                  </div>
                  <div className="need">{left ? t('prizes.car.need', { count: left }) : t('prizes.car.ready')}</div>
                </>
              )}
            </div>
            <div className="drawbar">
              <p>{t('prizes.car.drawInfo')}</p>
              <b>{t('prizes.car.drawBadge')}</b>
            </div>
          </div>
        </div>

        <PromoBand />
      </div>
    </section>
  );
}

function SideCard({
  prizeKey,
  img,
  delay,
  titleKey,
  subtitleKey,
  onOpen,
}: {
  prizeKey: Exclude<PrizeModalKey, 'car'>;
  img: string;
  delay: string;
  titleKey: 'prizes.cards.bonus.title' | 'prizes.cards.brand.title' | 'prizes.cards.biz.title' | 'prizes.cards.tech.title';
  subtitleKey: 'prizes.cards.bonus.subtitle' | 'prizes.cards.brand.subtitle' | 'prizes.cards.biz.subtitle' | 'prizes.cards.tech.subtitle';
  onOpen: (key: PrizeModalKey) => void;
}) {
  const { t } = useTranslation('landing');
  const ref = useRef<HTMLElement>(null);
  useTilt(ref, 10);

  return (
    <article className={`pcard tilt rv ${delay}`} ref={ref} data-prize={prizeKey} onClick={() => onOpen(prizeKey)}>
      <div className="pic">
        <img src={img} alt="" />
      </div>
      <div className="cap">
        <b>{t(titleKey)}</b>
        <small>{t(subtitleKey)}</small>
        <span className="wk">
          <i className="ico i-snow" />
          {t('prizes.cardBadge')}
        </span>
      </div>
      <button
        type="button"
        className="infoi"
        aria-label={t('prizes.infoAria')}
        onClick={(e) => {
          e.stopPropagation();
          onOpen(prizeKey);
        }}
      >
        i
      </button>
      <i className="glare" />
    </article>
  );
}

function PromoBand() {
  const { t } = useTranslation('landing');
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % 3), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="promoband rv">
      <i className="ico i-goat wm" />
      <div className="pl">
        <span className="ptag">
          <i className="ico i-bows" />
          {t('prizes.promoband.tag')}
        </span>
        <h3>{t('prizes.promoband.title')}</h3>
        <p>{t('prizes.promoband.text')}</p>
        <div className="pflow lot">
          <div className={step === 0 ? 'pf on' : 'pf'}>
            <span className="pn">1</span>
            <div className="pui">
              <span className="lchip">
                <i className="ico i-run" />
                {t('prizes.promoband.steps.step1.badge')}
              </span>
            </div>
            <b>{t('prizes.promoband.steps.step1.title')}</b>
            <small>{t('prizes.promoband.steps.step1.sub')}</small>
          </div>
          <div className={step === 1 ? 'pf on' : 'pf'}>
            <span className="pn">2</span>
            <div className="pui">
              <span className="lbox">
                <i className="ico i-bow" />
              </span>
            </div>
            <b>{t('prizes.promoband.steps.step2.title')}</b>
            <small>{t('prizes.promoband.steps.step2.sub')}</small>
          </div>
          <div className={step === 2 ? 'pf on' : 'pf'}>
            <span className="pn">3</span>
            <div className="pui">
              <span className="lwin">🎧</span>
              <span className="lor">{t('prizes.promoband.steps.step3.or')}</span>
              <span className="lwin pc">%</span>
            </div>
            <b>{t('prizes.promoband.steps.step3.title')}</b>
            <small>{t('prizes.promoband.steps.step3.sub')}</small>
          </div>
        </div>
        <div className="pnote">{t('prizes.promoband.note')}</div>
      </div>
      <div className="tickets">
        <div className="ticket t1">
          <div className="tv">🎧</div>
          <div className="td">
            <b>{t('prizes.promoband.tickets.tech.title')}</b>
            <span>{t('prizes.promoband.tickets.tech.sub')}</span>
          </div>
        </div>
        <div className="ticket t2">
          <div className="tv">💰</div>
          <div className="td">
            <b>{t('prizes.promoband.tickets.bonus.title')}</b>
            <span>{t('prizes.promoband.tickets.bonus.sub')}</span>
          </div>
        </div>
        <div className="ticket t1">
          <div className="tv">🎁</div>
          <div className="td">
            <b>{t('prizes.promoband.tickets.merch.title')}</b>
            <span>{t('prizes.promoband.tickets.merch.sub')}</span>
          </div>
        </div>
        <div className="ticket t3">
          <div className="tv">−90%</div>
          <div className="td">
            <b>{t('prizes.promoband.tickets.promo.title')}</b>
            <span>{t('prizes.promoband.tickets.promo.sub')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
